import { makeStyles } from '@material-ui/core/styles';
import { TextColors, TextOpacities, SecondaryColors, spacingSizesMap } from '@/utils/constants';
import { hexToRGBA } from '@/utils/helpers';

const darkTheme = {
  root: {
    '& $content': {
      color: TextColors.light,
    },
    '& $tabs': {
      '&:not(.MuiTabs-vertical)': {
        '&:before': {
          borderBottomColor: hexToRGBA(TextColors.light, TextOpacities.light3),
        },
      },

      '&.MuiTabs-vertical': {
        '&:before': {
          borderLeftColor: hexToRGBA(TextColors.light, TextOpacities.light3),
        },

        '& .MuiTabs-indicator': {
          backgroundColor: SecondaryColors.main,
        },
      },
    },
    '& $tab': {
      '&.MuiTab-textColorInherit': {
        color: hexToRGBA(TextColors.light, TextOpacities.light2),

        '&.Mui-selected, &:hover': {
          color: hexToRGBA(TextColors.light),
        },
      },
    },
    '& $superheading': {
      color: SecondaryColors.main,
    },
    '& $rtc': {
      color: hexToRGBA(TextColors.light, TextOpacities.light2),

      '& b, & strong': {
        color: TextColors.light,
      },
    },
    '& $title': {
      color: SecondaryColors.main,
    },
  },
};

const lightTheme = {
  root: {
    '& $content': {
      color: TextColors.dark,
    },
    '& $tabs': {
      '&:not(.MuiTabs-vertical)': {
        '&:before': {
          borderBottomColor: hexToRGBA(TextColors.dark, TextOpacities.dark3),
        },
      },

      '&.MuiTabs-vertical': {
        '&:before': {
          borderLeftColor: hexToRGBA(TextColors.dark, TextOpacities.dark3),
        },

        '& .MuiTabs-indicator': {
          backgroundColor: SecondaryColors.dark,
        },
      },
    },
    '& $tab': {
      '&.MuiTab-textColorInherit': {
        color: hexToRGBA(TextColors.dark, TextOpacities.dark2),

        '&.Mui-selected, &:hover': {
          color: hexToRGBA(TextColors.dark),
        },
      },
    },
    '& $superheading': {
      color: SecondaryColors.dark,
    },
    '& $rtc': {
      color: hexToRGBA(TextColors.dark, TextOpacities.dark2),

      '& b, & strong': {
        color: TextColors.dark,
      },
    },
    '& $title': {
      color: SecondaryColors.dark,
    },
  },
};

const useStyles = makeStyles(
  (muiTheme) => ({
    trigger: {
      height: '50vh',

      '&:first-child': {
        height: '50vh',
      },

      '&:last-child': {
        height: '100vh',
      },
    },
    content: {
      [muiTheme.breakpoints.up('md')]: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        alignContent: 'flex-start',
        justifyContent: 'center',
        paddingTop: spacingSizesMap.medium,
        paddingBottom: spacingSizesMap.medium,
        minHeight: 'calc(100vh - 58px)',
      },

      '& .tabs': {
        position: 'relative',

        [muiTheme.breakpoints.up('md')]: {
          width: 'calc(100% - 32px)',
          maxWidth: '1211px',
        },
      },
    },
    root: {
      height: 'auto',

      '&.is-sticky': {
        [muiTheme.breakpoints.up('md')]: {
          position: 'sticky',
          top: '58px',
          minHeight: 'calc(100vh - 58px)',
        },
      },
    },
    tabsRoot: {
      [muiTheme.breakpoints.down('sm')]: {
        '&.light-theme': lightTheme.root,
        '&.dark-theme': darkTheme.root,
      },
      [muiTheme.breakpoints.up('md')]: {
        '&.light-theme-md': lightTheme.root,
        '&.dark-theme-md': darkTheme.root,
      },
    },
    tabs: {
      position: 'relative',

      '&:not(.MuiTabs-vertical)': {
        display: 'inline-flex',
        padding: '2px 2px 0px 0px',
        margin: '-2px -2px 0px -32px',
        width: 'calc(100% + 64px)',

        '&:before': {
          content: '""',
          borderBottomWidth: '1px',
          borderBottomStyle: 'solid',
          width: 'calc(100% - 64px)',
          position: 'absolute',
          top: 0,
          left: '32px',
          bottom: 0,
        },

        '& .MuiTabs-scrollButtons': {
          width: '32px',
        },

        '& + *': {
          marginTop: '28px',
        },
      },

      '&.MuiTabs-vertical': {
        padding: '2px 2px 2px 0',
        margin: '-2px -2px -2px 0',

        '&:before': {
          content: '""',
          borderLeftWidth: '1px',
          borderLeftStyle: 'solid',
          height: '100%',
          position: 'absolute',
          top: 0,
          left: 0,
        },

        '& .MuiTabs-indicator': {
          height: '28px !important',
          width: '1px',
          left: 0,
          zIndex: 1,
        },

        '& .MuiTabs-flexContainer': {
          paddingLeft: '6px',

          [muiTheme.breakpoints.up('md')]: {
            display: 'inline-flex',
          },
        },

        '& .MuiTabs-fixed': {
          padding: '2px 2px 2px 0',
          margin: '-2px -2px -2px 0',
        },
      },
    },
    tab: {
      '&.MuiTab-root': {
        maxWidth: '300px',

        [muiTheme.breakpoints.up('md')]: {
          whiteSpace: 'nowrap',
        },
      },

      '& .MuiTypography-body1': {
        [muiTheme.breakpoints.down('sm')]: {
          lineHeight: '1em',
        },
      },

      '&:not([aria-selected="true"])': {
        height: '0',

        [muiTheme.breakpoints.up('md')]: {
          '& > *:last-child:not(:first-child)': {
            opacity: 0,
            visibility: 'hidden',
          },
        },
      },

      '&.MuiTab-textColorInherit': {
        opacity: 1,
      },

      [muiTheme.breakpoints.up('md')]: {
        '&.MuiTab-root': {
          flexDirection: 'column',
          alignItems: 'flex-start',
          alignContent: 'flex-start',
          justifyContent: 'flex-start',
          textAlign: 'left',
          minHeight: '28px',
          maxWidth: '100%',
          paddingTop: '1.5px',
          paddingBottom: '1.5px',

          '& + *': {
            marginTop: '14px',
          },

          '& > *:last-child': {
            transition: 'opacity 150ms ease-in-out',
          },
        },
      },
    },
    tabPanels: {
      '&.light-theme': lightTheme.root,
      '&.dark-theme': darkTheme.root,
      marginTop: '32px',

      [muiTheme.breakpoints.up('md')]: {
        display: 'flex',
        position: 'relative',
        alignItems: 'flex-end',
        alignContent: 'flex-end',
        width: '100%',
        marginTop: '100px',
      },
    },
    tabPanel: {},
    tabPanelDetailContent: {
      flexShrink: 0,

      [muiTheme.breakpoints.up('md')]: {
        boxShadow: muiTheme.shadows[8],
        borderRadius: '4px',
        padding: muiTheme.spacing(10, 12),
        maxWidth: '542px',
        height: '445px',
        marginRight: '140px',
        marginBottom: '48px',
        width: '100%',
      },
    },
    tabPanelCarouselContent: {
      '&.has-text-shadow': {
        filter: `
          drop-shadow(0px 5px 5px rgba(0, 0, 0, 0.2))
          drop-shadow(0px 8px 10px rgba(0, 0, 0, 0.14))
          drop-shadow(0px 3px 14px rgba(0, 0, 0, 0.12))
        `,
      },

      [muiTheme.breakpoints.down('sm')]: {
        marginLeft: '-32px',
        marginRight: '-32px',
        marginTop: '32px',
      },

      [muiTheme.breakpoints.up('md')]: {
        maxWidth: 'calc(100% - 682px)',
      },
    },
    tabPanelAnimated: {
      '& > *': {
        animation: 'enter 250ms ease-out',
      },
    },
    superheading: {
      '&.MuiTypography-root': {
        marginBottom: muiTheme.spacing(2),
      },
    },
    heading: {
      marginTop: 0,

      '& p, & h1, & h2, & h3, & h4, & h5, & h6': {
        ...muiTheme.typography.h4,
      },

      '& + *': {
        marginTop: muiTheme.spacing(2),
      },
    },
    rtc: {},
    link: {
      '&:not(:first-child)': {
        marginTop: muiTheme.spacing(4),

        [muiTheme.breakpoints.down('sm')]: {
          width: '100%',
        },
      },
    },
    title: {
      '&.MuiTypography-root': {
        marginBottom: '12px',

        [muiTheme.breakpoints.up('md')]: {
          marginBottom: '38px',
        },
      },
    },
    slider: {
      '& .slick-track': {
        display: 'flex !important',

        '& .slick-slide': {
          '& > div': {
            height: 'auto',
            margin: muiTheme.spacing(1),
          },
        },
      },

      '& [slot="container-end"]': {
        [muiTheme.breakpoints.up('md')]: {
          paddingLeft: '36px',
        },
      },
    },
    sliderCard: {
      height: '220px !important',
      width: '220px !important',
      textAlign: 'left',
      display: 'block',
    },
    sliderNavigation: {
      position: 'static',
    },
    dotsArrowsContainer: {
      display: 'flex',
      flexFlow: 'row nowrap',
      alignItems: 'center',

      [muiTheme.breakpoints.down('sm')]: {
        margin: '0 auto',
      },
    },
  }),
  { name: 'GenVerticalWithCarousel' },
);

export default useStyles;
